import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, Check, CreditCard, Info, Instagram } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import cn from "~/utils/cn";
import Badge from "../../ui/Badge";
import Button from "../../ui/Button";
import { formatMoney, formatNumber, formatPercent, type PlanView } from "../../plans";
import { EquivalentArt, EquivalentLine } from "../../equivalents/PriceEquivalent";
import { planEquivalents } from "../../equivalents/pick";
import { checkoutHref, INSTAGRAM_URL, type LandingData } from "../types";
import SectionTitle from "../shared/SectionTitle";

const glass =
  "rounded-lg bg-[linear-gradient(160deg,color-mix(in_srgb,var(--c-raised)_55%,transparent)_0%,color-mix(in_srgb,var(--c-bg-2)_70%,transparent)_100%)] shadow-card ring-1 ring-inset ring-line backdrop-blur-[22px] backdrop-saturate-150";
const picked =
  "bg-[linear-gradient(160deg,color-mix(in_srgb,var(--c-accent)_34%,transparent)_0%,color-mix(in_srgb,var(--c-bg-2)_72%,transparent)_100%)] ring-[1.5px] ring-accent-text";

// Glass picker, cheapest first: the monthly plan is the dominant tile, longer plans are compact rows beside it, and the checkout
// bar below follows the choice (the monthly plan to start).
export default function PlansA({ data }: { data: LandingData }) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  // the monthly plan leads (most visitors look for it first); the "best value" badge stays on the yearly one
  const best = data.plans[0];
  const rest = data.plans.slice(1);
  const [selected, setSelected] = useState(data.plans[0]?.plan);
  const current = data.plans.find((p) => p.plan === selected) ?? data.plans[0];
  // everyday item at the same price, e.g. "به قیمت یه همبرگر" (only when the Toman price is known)
  const eqs = planEquivalents(data.plans, data.currency?.code);
  const bestEq = best ? eqs[best.id] : null;
  const currentEq = current ? eqs[current.id] : null;

  const price = (p: PlanView) => (p.total !== null && data.currency ? formatMoney(p.total, data.currency) : null);
  const saving = (p: PlanView) => (p.saving >= 0.01 ? t("ui.landing.plans.saving", { percent: formatPercent(p.saving) }) : null);
  const months = (p: PlanView) => t("ui.landing.plans.months", { count: formatNumber(p.months) } as Record<string, string>);

  return (
    <section
      id="plans"
      aria-labelledby="plans-title"
      className="relative isolate scroll-mt-[var(--nav-h)] overflow-hidden pb-[clamp(96px,11vw,168px)] pt-12 md:pt-20"
    >
      {/* a soft glow behind the monthly tile gives the glass something to blur */}
      <span
        aria-hidden
        className="pointer-events-none absolute start-[-10%] top-[22%] -z-[1] h-[640px] w-[min(120%,900px)] bg-[radial-gradient(closest-side,color-mix(in_srgb,var(--c-accent)_38%,transparent),transparent)] opacity-70"
      />

      <div className="mx-auto w-full max-w-[1200px] px-[var(--gutter)]">
        <SectionTitle id="plans-title" title={t("ui.landing.plans.title")} lead={t("ui.landing.plans.subtitle")} className="mb-9 md:mb-16" />

        {!best || !current ? (
          <div className={cn(glass, "flex flex-col items-start gap-5 p-6 md:flex-row md:items-center md:justify-between md:p-8")}>
            <p className="max-w-[46ch] text-[17px] leading-[1.9] text-ink-2">{t("ui.landing.plans.unavailable")}</p>
            <Button href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" variant="secondary" className="h-12">
              <Instagram className="size-5" strokeWidth={1.75} aria-hidden />
              {t("ui.landing.faq.instagram")}
            </Button>
          </div>
        ) : (
          <>
            <div role="radiogroup" aria-labelledby="plans-title" className="grid gap-3 md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
              <Option
                plan={best}
                selected={selected}
                onSelect={setSelected}
                check={!bestEq} // the art takes the check's corner; the picked ring still marks the choice
                className={cn("flex flex-col justify-between gap-7 p-6 md:min-h-[400px] md:gap-10 md:p-10", bestEq && "min-h-[290px]")}
              >
                {bestEq && <EquivalentArt equivalent={bestEq} size={150} className="absolute end-4 top-4 !size-[104px] md:end-8 md:top-8 md:!size-[164px]" />}
                {!bestEq && (
                  <span aria-hidden className="pointer-events-none absolute end-10 top-1/2 -mt-[.6em] hidden font-display text-[clamp(4.25rem,2.4rem+6vw,9rem)] font-black leading-none text-transparent [-webkit-text-stroke:1px_color-mix(in_srgb,var(--c-accent-text)_24%,transparent)] md:block">
                    {formatNumber(30)}
                  </span>
                )}
                <span className={cn("relative flex flex-wrap items-center gap-3", bestEq ? "pe-[112px] md:pe-[180px]" : "pe-12")}>
                  <span className="font-display text-[1.75rem] font-extrabold leading-[1.4]">{best.name}</span>
                  {best.isBest && (
                    <Badge tone="info" className="h-[30px] px-3 font-display text-sm font-bold">
                      {t("ui.landing.plans.best")}
                    </Badge>
                  )}
                </span>
                <span className="relative">
                  {price(best) ? (
                    <span className="flex flex-wrap items-baseline gap-2.5">
                      <span className="font-display text-[clamp(3rem,2.2rem+3.4vw,4.75rem)] font-black leading-[1.15]">{formatNumber(best.total!)}</span>
                      <span className="text-lg text-ink-2">{data.currency!.symbol}</span>
                    </span>
                  ) : (
                    <span className="block font-display text-[clamp(3rem,2.2rem+3.4vw,4.75rem)] font-black leading-[1.15]">{months(best)}</span>
                  )}
                  <span className="mt-2 flex flex-wrap items-baseline gap-x-[18px] gap-y-1 text-ink-2">
                    {bestEq && <EquivalentLine equivalent={bestEq} className="text-lg [&>b]:text-[1.375rem] md:[&>b]:text-[1.625rem]" />}
                    {saving(best) && <span className="font-medium text-accent-text">{saving(best)}</span>}
                  </span>
                </span>
              </Option>

              <div className="grid auto-rows-fr gap-3">
                {rest.map((p) => (
                  <Option
                    key={p.plan}
                    plan={p}
                    selected={selected}
                    onSelect={setSelected}
                    className="grid min-h-20 grid-cols-[auto_minmax(0,1fr)] items-center gap-3.5 px-[18px] py-3.5 md:min-h-[88px] md:gap-4 md:px-[22px]"
                  >
                    {eqs[p.id] ? (
                      <EquivalentArt
                        equivalent={eqs[p.id]!}
                        size={56}
                        className={cn("-my-2 transition-[filter,opacity] duration-300", p.plan === selected ? "" : "opacity-75 saturate-[.7]")}
                      />
                    ) : (
                      <span
                        aria-hidden
                        className={cn("grid size-6 place-items-center rounded-full ring-[1.5px] ring-inset transition-shadow duration-200", p.plan === selected ? "ring-accent-text" : "ring-field-line")}
                      >
                        <span className={cn("size-2.5 rounded-full bg-accent-text transition-transform duration-[400ms] ease-out", p.plan === selected ? "scale-100" : "scale-0")} />
                      </span>
                    )}
                    {/* name and price on top; the everyday item gets the full width below, the saving keeps the price's end */}
                    <span className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 leading-[1.6]">
                      <span className="flex min-w-0 flex-wrap items-center gap-2">
                        <span className="font-display text-lg font-extrabold md:text-[1.375rem]">{p.name}</span>
                        {p.isBest && <Badge tone="info">{t("ui.landing.plans.best")}</Badge>}
                      </span>
                      {price(p) ? (
                        <span className="whitespace-nowrap font-display text-lg font-extrabold md:text-xl">
                          {formatNumber(p.total!)}
                          <small className="ms-1 font-body text-sm font-normal text-ink-2">{data.currency!.symbol}</small>
                        </span>
                      ) : (
                        <span />
                      )}
                      <span className="col-span-2 flex flex-wrap items-baseline gap-x-3">
                        {eqs[p.id] && <EquivalentLine equivalent={eqs[p.id]!} className="text-[15px] [&>b]:text-base md:[&>b]:text-[17px]" />}
                        {saving(p) && <span className={cn("text-[15px] font-medium text-accent-text", price(p) && "ms-auto")}>{saving(p)}</span>}
                      </span>
                    </span>
                  </Option>
                ))}
              </div>
            </div>

            <div className={cn(glass, "mt-3 flex flex-col gap-4 p-3.5 md:flex-row md:items-center md:justify-between md:gap-5 md:ps-6")}>
              <div className="flex min-w-0 items-center gap-3.5 px-1 pt-1 md:p-0">
                {/* the chosen plan's art is already in the tile or row above, so the bar keeps a plain icon */}
                <span className="grid size-11 shrink-0 place-items-center rounded-sm bg-accent-soft text-accent-text">
                  <CreditCard className="size-[22px]" strokeWidth={1.75} aria-hidden />
                </span>
                <p className="flex min-w-0 flex-col leading-[1.6]" aria-live="polite">
                  <b className="font-display font-bold">{current.name}</b>
                  {currentEq ? (
                    <EquivalentLine equivalent={currentEq} className="text-[15px] [&>b]:text-base" />
                  ) : (
                    !price(current) && <span className="text-[15px] text-ink-2">{t("ui.landing.plans.guestPrice")}</span>
                  )}
                </p>
              </div>
              <Button to={checkoutHref(current.plan)} prefetch="intent" size="lg" className="h-[60px] w-full overflow-hidden px-6 font-display text-base md:w-auto md:px-8 md:text-lg">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span
                    key={current.plan}
                    className="inline-flex items-center gap-3"
                    initial={reduce ? false : { y: "70%", opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={reduce ? { opacity: 1 } : { y: "-70%", opacity: 0, transition: { duration: 0.14, ease: [0.7, 0, 0.84, 0] } }}
                    transition={{ duration: reduce ? 0 : 0.28, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {t("ui.landing.plans.buy", { name: current.name })}
                    {price(current) && (
                      <>
                        <span aria-hidden className="h-[22px] w-px bg-[color-mix(in_srgb,var(--c-on-accent)_38%,transparent)]" />
                        <span>{price(current)}</span>
                      </>
                    )}
                  </motion.span>
                </AnimatePresence>
                <ArrowLeft className="hidden size-5 shrink-0 ltr:rotate-180 md:block" strokeWidth={1.75} aria-hidden />
              </Button>
            </div>
          </>
        )}

        <p className="mt-5 flex max-w-[60ch] gap-2.5 text-[15px] leading-[1.9] text-ink-3">
          <Info className="mt-[5px] size-[18px] shrink-0" strokeWidth={1.75} aria-hidden />
          {t("ui.landing.plans.paymentNote")}
        </p>
      </div>
    </section>
  );
}

// One radio: a native input (keyboard arrows, radiogroup semantics) inside a label styled as glass.
function Option({
  plan,
  selected,
  onSelect,
  check,
  className,
  children,
}: {
  plan: PlanView;
  selected: string | undefined;
  onSelect: (p: PlanView["plan"]) => void;
  check?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const on = plan.plan === selected;
  return (
    <label
      className={cn(
        glass,
        "relative cursor-pointer overflow-hidden transition-[transform,box-shadow,background-color] duration-300 ease-out active:scale-[0.985] has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-[3px] has-[:focus-visible]:outline-accent-text [@media(hover:hover)]:hover:-translate-y-0.5",
        on && picked,
        className,
      )}
    >
      <input type="radio" name="landing-plan" value={plan.plan} checked={on} onChange={() => onSelect(plan.plan)} className="sr-only" />
      {check && (
        <span
          aria-hidden
          className={cn(
            "absolute end-6 top-6 z-[2] grid size-[34px] place-items-center rounded-full bg-accent text-on-accent transition-transform duration-[450ms] ease-out md:end-10 md:top-10",
            on ? "scale-100" : "scale-0",
          )}
        >
          <Check className="size-5" strokeWidth={2} />
        </span>
      )}
      {children}
    </label>
  );
}

